import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { Redirect, Route, Router, Switch, useLocation } from "wouter";

import AppShell from "@/components/shared/AppShell";
import BootLoading from "@/components/shared/BootLoading";
import KeychainLocked from "@/components/shared/KeychainLocked";
import ServerUnreachable from "@/components/shared/ServerUnreachable";
import SignedOutNotice from "@/components/shared/SignedOutNotice";
import SignOutUnconfirmed from "@/components/shared/SignOutUnconfirmed";

// Routes are React.lazy'd so each view ships its own chunk and the
// initial download covers only the shell + AuthGate. The eager imports
// below are the always-needed shell pieces (Login lives off the auth
// gate; everything else is conditional on a route match).
import { RequireSuperAdmin } from "@/auth/RequireSuperAdmin";
import AdminConsoleRoutes from "@/views/admin/AdminConsoleRoutes";
import { ADMIN_BASE } from "@/views/admin/adminRoutes";
const HomeView = lazy(() => import("@/components/Home/HomeView"));
const WhatsNextView = lazy(() => import("@/components/WhatsNext/WhatsNextView"));
const EditorTabsView = lazy(() => import("@/components/Editor/EditorTabsView"));
const LaunchView = lazy(() => import("@/components/Runs/LaunchView"));
const LaunchDocStoreProvider = lazy(
  () => import("@/components/Runs/launchView/LaunchDocStoreProvider"),
);
const RunsTabsView = lazy(() => import("@/components/Runs/RunsTabsView"));
const BotsView = lazy(() => import("@/views/Bots"));
const BotHomeView = lazy(() => import("@/views/Bots/BotHome"));
const BotBuilderView = lazy(() => import("@/views/Bots/BotBuilder"));
const BoardView = lazy(() => import("@/views/Board"));
const PipelineBoardView = lazy(() => import("@/views/PipelineBoard"));
const LabelsView = lazy(() => import("@/views/Board/Labels"));
const FieldsView = lazy(() => import("@/views/Board/Fields"));
const RunsAnalyticsView = lazy(() => import("@/views/RunsAnalytics"));
const DispatcherView = lazy(() => import("@/views/Dispatcher"));
const TriggersView = lazy(() => import("@/views/Triggers"));
const MarketplaceView = lazy(() => import("@/views/Marketplace"));
const PluginsView = lazy(() => import("@/views/Plugins"));
const SecretsView = lazy(() => import("@/views/Secrets"));
const SkillsView = lazy(() => import("@/views/Skills"));
const ConfigEditorView = lazy(() => import("@/views/ConfigEditor"));
const Welcome = lazy(() => import("@/views/Welcome"));
const SettingsDialog = lazy(() => import("@/views/SettingsDialog"));
const ProjectSwitcher = lazy(() => import("@/views/ProjectSwitcher"));
const CloudReloginModal = lazy(() => import("@/components/shared/CloudReloginModal"));
const SitemapPage = lazy(() => import("@/views/Sitemap"));
const SettingsPage = lazy(() => import("@/views/account/SettingsPage"));
const TeamPage = lazy(() => import("@/views/teams/TeamPage"));
const IntegrationsPage = lazy(() => import("@/views/integrations/IntegrationsPage"));
const ConnectRepoWizardView = lazy(() => import("@/views/integrations/ConnectRepoWizard"));
const BindBotWizardView = lazy(() => import("@/views/integrations/BindBotWizard"));
const RepoDetailView = lazy(() => import("@/views/RepoDetail"));
const OrgPage = lazy(() => import("@/views/orgs/OrgPage"));

// Auth side-doors reachable when anonymous (forced password rotation,
// forgot/reset password) and when authed (invitation accept).
const ForcedPasswordChange = lazy(() => import("@/views/auth/ForcedPasswordChange"));
const ForgotPassword = lazy(() => import("@/views/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/views/auth/ResetPassword"));
const AcceptInvitation = lazy(() => import("@/views/auth/AcceptInvitation"));
const Login = lazy(() => import("@/views/Login"));

import { AssistantProvider } from "@/components/ChatDock/AssistantProvider";
import ChatDock, {
  AssistantDockCrashed,
} from "@/components/ChatDock/ChatDock";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import FeatureUnavailable from "@/components/shared/FeatureUnavailable";
import GlobalCommandPalette from "@/components/shared/GlobalCommandPalette";
import ToastContainer from "@/components/shared/Toast";
import MissingCLIBanner from "@/components/MissingCLIBanner";
import CloudLanding, { PublicTopBar } from "@/views/CloudLanding";
const RestrictedShell = lazy(() => import("@/views/RestrictedShell"));
const ConfigEditorShell = lazy(() => import("@/views/ConfigEditorShell"));
const CliAuthPage = lazy(() => import("@/views/CliAuthPage"));
const ConfigShareView = lazy(() => import("@/views/ConfigShare"));
import { useDesktop } from "@/hooks/useDesktop";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useProjectSwitchListener } from "@/hooks/useProjectSwitchListener";
import { useProjectScopeSync } from "@/hooks/useProjectScopeSync";
import { onDesktopEvent } from "@/lib/desktopBridge";
import { DesktopEvent } from "@/lib/desktopEvents";
import { STUDIO_BASE, isScopedPane, scopePrefix } from "@/lib/scope";
import { showRunAlertNotification, type RunAlertPayload } from "@/lib/desktopNotify";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import type { ServerInfo } from "@/api/types";
import { signInURL } from "@/auth/returnTo";
import { setUnauthorizedHandler } from "@/api/client";
import { getOrCreateDocumentStore } from "@/store/document";
import { editorTabOnScreen } from "@/components/Editor/editorOnScreen";
import { useServerInfoStore } from "@/store/serverInfo";

// activeEditorDocStore looks up the document store of the editor tab on
// screen. Null when no editor tab is on screen — another page, or the
// editor's welcome pane: the menu's Undo / Redo then do nothing rather than
// change a document the author cannot see.
function activeEditorDocStore() {
  const tabId = editorTabOnScreen();
  return tabId ? getOrCreateDocumentStore(tabId) : null;
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
      {/* Pinned over whatever the gate renders, and mutually exclusive by
          construction: a sign-out sets no notice, and a notice sets no
          sign-out mark (AuthContext). */}
      <SignOutUnconfirmed />
      <SignedOutNotice />
    </AuthProvider>
  );
}

// ScopedPaneReauth is what a workspace pane shows when its cloud session
// expired: it signals the shell (parent frame) to prompt re-login — the pane
// can't (no window.go) — and waits for the shell to re-arm the token + reload.
function ScopedPaneReauth() {
  useEffect(() => {
    const connId = scopePrefix().replace(/^\/x\//, "");
    window.parent?.postMessage({ source: "iterion-pane", type: "auth-expired", connId }, "*");
  }, []);
  return (
    <div className="h-screen flex flex-col items-center justify-center gap-2 bg-surface-0 text-fg-muted px-6 text-center">
      <p className="text-sm font-medium text-fg-default">Session expired</p>
      <p className="text-xs">Reconnect from the desktop — the sign-in prompt should appear.</p>
    </div>
  );
}

// StudioSignIn bounces an anonymous visitor who followed a studio deep link
// (a run URL out of mail or a pull-request comment) to the sign-in page, with
// the address they wanted carried in ?next= so the login lands them on it.
// window.location rather than the router's location: `next` travels through
// the server and comes back as a redirect target, so it has to be the absolute
// path, pane scope included.
function StudioSignIn() {
  const { pathname, search, hash } = window.location;
  return <Redirect to={signInURL(pathname + search + hash)} replace />;
}

// ROOT_SIDE_DOORS are the paths that answer at the root even for a signed-in
// operator, because their addresses are already fixed somewhere this app does
// not control: printed in mail (/auth/reset, /invitations/accept), minted by
// the CLI (/cli-auth), handed out as a share link whose token rides in the
// fragment (/config/<id>), or linked to from the product home (/login).
// Moving any of them under the studio base breaks a link nobody can reissue.
const ROOT_SIDE_DOORS = ["/login", "/auth", "/invitations", "/cli-auth", "/config"];

function isRootSideDoor(location: string): boolean {
  return ROOT_SIDE_DOORS.some((p) => location === p || location.startsWith(p + "/"));
}

function isUnderStudioBase(location: string): boolean {
  return location === STUDIO_BASE || location.startsWith(STUDIO_BASE + "/");
}

// PasswordDoors are the password side-doors: they authenticate on the token
// in the URL, not on the session, so they answer whatever the session is.
// otherwise renders for an /auth/ path that names none of them.
function PasswordDoors({ otherwise }: { otherwise: ReactNode }) {
  return (
    <Suspense fallback={<BootLoading />}>
      <Switch>
        <Route path="/auth/password/change" component={ForcedPasswordChange} />
        <Route path="/auth/forgot-password" component={ForgotPassword} />
        <Route path="/auth/reset" component={ResetPassword} />
        <Route>{otherwise}</Route>
      </Switch>
    </Suspense>
  );
}

// ConfigShareDoor is the shell-less config-share editor, which authenticates
// on the share token alone.
function ConfigShareDoor() {
  return (
    <Suspense fallback={<BootLoading />}>
      {/* Wrapped in its Route, because ConfigShareView reads the share id
          with useParams(), which only a matching <Route> supplies — rendered
          bare, a perfectly good /config/<id> link reported "The share id is
          missing from the URL". The anonymous arm has always been wrapped;
          this one never was.
          The fall-through matters as much as the route: that message is
          ConfigShareView's own, correct answer to a link with NO id, and a
          bare <Switch> with one route renders NOTHING when it misses —
          /config/ and /config/a/b became an empty page with no way out. */}
      <Switch>
        <Route path="/config/:id">
          <ErrorBoundary area="Config share editor">
            <ConfigShareView />
          </ErrorBoundary>
        </Route>
        <Route>
          <ErrorBoundary area="Config share editor">
            <ConfigShareView />
          </ErrorBoundary>
        </Route>
      </Switch>
    </Suspense>
  );
}

// PublicMarketplace is the public catalogue, outside the AppShell, with its
// own slim top bar.
function PublicMarketplace() {
  return (
    <div className="min-h-screen bg-surface-0 text-fg-default">
      <PublicTopBar />
      <ErrorBoundary area="Marketplace view">
        <MarketplaceView />
      </ErrorBoundary>
    </div>
  );
}

// identityIndependentDoor is what a browser whose session's identity the
// server cannot read right now may still open: the doors that do not rest on
// that identity. null for every other address.
function identityIndependentDoor(location: string, serverInfo: ServerInfo | null): ReactNode | null {
  if (location === "/login") {
    return <Suspense fallback={<BootLoading />}><Login /></Suspense>;
  }
  if (location === "/auth/password/change" || location === "/auth/forgot-password" || location === "/auth/reset") {
    return <PasswordDoors otherwise={null} />;
  }
  if (location.startsWith("/config/")) {
    return <ConfigShareDoor />;
  }
  if (serverInfo?.marketplace_enabled && (location === "/marketplace" || location === `${STUDIO_BASE}/marketplace`)) {
    return <PublicMarketplace />;
  }
  return null;
}

// AuthGate decides between the Login view and the full editor based
// on the AuthProvider's status. It also wires the global 401
// interceptor so editor API calls bounce the user back to /login on
// session expiration.
//
// Side-doors: a small set of paths (forgot-password, reset, forced
// password change, invitation accept) must be reachable WITHOUT a
// session so the AuthGate consults the URL when it sees the
// "anonymous" state and dispatches to the matching public view.
function AuthGate() {
  const { status, signOut, isRestricted, activeRole, retryConnection, retryKeychainUnlock, signOutUnconfirmed } = useAuth();
  const [location] = useLocation();
  const serverInfo = useServerInfoStore((s) => s.info);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      void signOut();
    });
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  if (status === "loading") {
    return <BootLoading />;
  }
  // A sign-out on the wire shows nothing of the account — not the shell, not
  // the identity-unavailable screen and its auto-retry — until its logout
  // settled.
  if (status === "signing_out") {
    return <BootLoading label="Signing out" />;
  }
  // Backend down ≠ signed out: show the reconnect screen, never the
  // sign-in form (a local no-auth operator has no credentials to give).
  if (status === "unreachable") {
    return <ServerUnreachable onRetry={retryConnection} />;
  }
  // Identity unavailable ≠ signed out, and ≠ "you belong to nothing": the
  // session is valid, the server could not say whose it is right now. The
  // doors that do not rest on that identity stay open — a password link and
  // a config-share link authenticate on the token in their URL, /login signs
  // in as another account, the marketplace is public — and the screen offers
  // a way out of the state: signing out.
  // A locked system keychain is neither of the two above: the session is fine,
  // the server is fine, and the credential is in a keychain this computer will
  // not open. It gets its own screen — and its own retry, the only one that
  // reads the keychain, since every read raises an OS unlock prompt. The
  // identity-independent doors stay open here as well: a /login, a password
  // link or a config share does not rest on that credential.
  if (status === "keychain_locked") {
    const door = identityIndependentDoor(location, serverInfo);
    if (door) return door;
    return <KeychainLocked onRetry={retryKeychainUnlock} />;
  }
  if (status === "identity_unavailable") {
    const door = identityIndependentDoor(location, serverInfo);
    if (door) return door;
    return (
      <ServerUnreachable
        onRetry={retryConnection}
        title="Your identity is unavailable"
        message="You are still signed in, but the server could not load your account and organizations right now. Retrying automatically…"
        secondaryAction={{ label: "Sign out", onClick: signOut }}
      />
    );
  }
  if (status === "anonymous") {
    // A workspace pane (iframe) can't run its own auth — the desktop owns the
    // token jar. On expiry, ask the shell (parent frame) to prompt re-login,
    // then reload us; don't render the pane's own Login/landing.
    if (isScopedPane()) {
      // A sign-out the desktop did not confirm left this connection's session
      // where it was: the pane says so (SignOutUnconfirmed, pinned over this
      // page) and signs out again from there, instead of asking the shell to
      // reconnect a session that is not over.
      return signOutUnconfirmed ? <div className="h-screen bg-surface-0" /> : <ScopedPaneReauth />;
    }
    return (
      <Suspense fallback={<BootLoading />}>
        <Switch>
          {/* Dedicated sign-in page: every "Sign in" button and the
              auth side-doors' navigate("/login") land on the plain
              SignInCard instead of scrolling the marketing landing. */}
          <Route path="/login" component={Login} />
          <Route path="/auth/password/change" component={ForcedPasswordChange} />
          <Route path="/auth/forgot-password" component={ForgotPassword} />
          <Route path="/auth/reset" component={ResetPassword} />
          <Route path="/invitations/accept" component={AcceptInvitation} />
          {/* Self-authenticating config-share editor. Renders shell-less
              (no AppShell/Sidebar/Header) — the visitor is NOT an
              operator, and the token in the URL fragment authenticates
              them. Mounted in the anonymous branch so a share link
              works without any session cookie. */}
          <Route path="/config/:id">
            <ErrorBoundary area="Config share editor">
              <ConfigShareView />
            </ErrorBoundary>
          </Route>
          {/* Public marketplace — browsable + downloadable without an
              account (submit/install gate behind sign-in). Outside the
              AppShell, so it carries its own slim top bar. */}
          {serverInfo?.marketplace_enabled && (
            <Route path="/marketplace">
              <PublicMarketplace />
            </Route>
          )}
          {/* The SAME public catalogue at the address a signed-in operator's
              address bar shows — they are carried to /studio/marketplace, and
              that is the URL they copy to a colleague. It must sit ABOVE the
              `${STUDIO_BASE}/*` sign-in catch below, or the public marketplace
              becomes a login wall for every link an operator shares. */}
          {serverInfo?.marketplace_enabled && (
            <Route path={`${STUDIO_BASE}/marketplace`}>
              <PublicMarketplace />
            </Route>
          )}
          {/* LAST before the product home, so every public route above —
              including the studio-prefixed marketplace — is matched first: a
              sign-in catch placed higher swallows them. A studio deep link
              followed without a session signs in, then lands on the page that
              was asked for. */}
          <Route path={STUDIO_BASE} component={StudioSignIn} />
          <Route path={`${STUDIO_BASE}/*`} component={StudioSignIn} />
          {/* Catch-all: the cloud product home (hero + sign-in card),
              degrading to the plain sign-in page in non-cloud modes. */}
          <Route>
            <CloudLanding />
          </Route>
        </Switch>
      </Suspense>
    );
  }
  // An existing session may reach a saved /login?next= link too.
  if (location === "/login") {
    return <Suspense fallback={<BootLoading />}><Login /></Suspense>;
  }
  // Authenticated paths that don't belong in the AppShell go here (the
  // invitation accept needs the AuthContext but not the full shell). Kept
  // reachable for restricted users so they can accept a team invitation.
  if (location.startsWith("/invitations/accept")) {
    return (
      <Suspense fallback={<BootLoading />}>
        <AcceptInvitation />
      </Suspense>
    );
  }
  // The password side-doors, reachable WITH a session too. They authenticate on
  // the token in the URL, not on the session, and swallowing the address —
  // which the /studio redirect below did — destroys the only copy of that
  // token the operator has: the mail link re-opens and is eaten again.
  if (location.startsWith("/auth/")) {
    return <PasswordDoors otherwise={<Redirect to={STUDIO_BASE} replace />} />;
  }
  // Browser half of `iterion remote login` — approve + mint a CLI token. Any
  // authenticated user (incl. the restricted tier) can authorize the CLI.
  if (location.startsWith("/cli-auth")) {
    return (
      <Suspense fallback={<BootLoading />}>
        <CliAuthPage />
      </Suspense>
    );
  }
  // Config-share editor — deliberately shell-less even when the operator is
  // signed in (opening the link in the same browser must NOT reveal any
  // studio chrome; the editor authenticates on the share token alone). The
  // isolated fetch client at @/api/configShare pins credentials: "omit", so
  // the operator's cookie is never sent even though same-origin.
  if (location.startsWith("/config/")) {
    return <ConfigShareDoor />;
  }
  // "/" is the product home for EVERYONE, signed in or not — that is what the
  // studio moving under a prefix buys. A deployment with no product home
  // (local, desktop, a self-hosted server not in cloud mode) has nothing to
  // show there, so its root sends the operator straight into the studio.
  if (location === "/") {
    // Wait for the probe rather than guess. Which of the two the root is
    // depends on the server mode, and the wrong guess here is not a flash of
    // the wrong view — it is a replace() into the studio that the operator
    // cannot come back from with the Back button.
    if (!serverInfo) {
      return <BootLoading />;
    }
    if (serverInfo.mode === "cloud") {
      return <Suspense fallback={<BootLoading />}><CloudLanding signedIn /></Suspense>;
    }
    return <Redirect to={STUDIO_BASE} replace />;
  }
  // Anything else still addressed at the root is a pre-move URL: a bookmark, a
  // link in mail sent before the move, or the public /marketplace followed
  // while signed in. The server answers these with a 302 as well
  // (pkg/server/studio_legacy_redirect.go); this arm is what covers the
  // desktop workspace panes, which the asset proxy serves without ever
  // reaching it.
  if (!isUnderStudioBase(location)) {
    // A root side-door reaching this line is one the branches above did not
    // claim — an /auth/… link opened with a live session. The page it names
    // exists for someone WITHOUT one, and prefixing it would address a route
    // the studio does not have, so that case lands on the studio root instead
    // of on a blank prefixed path.
    //
    // Rebuilt from window.location, not from wouter's location, in all three
    // parts. wouter decodeURI()s the path, which leaves the reserved set alone
    // but DOES decode %25 — so a segment carrying a literal percent came out
    // corrupted by the hop. The query and the fragment were never in wouter's
    // location at all, and a run link carries the tab to open and the node to
    // scroll to in exactly those two.
    // slice() on a prefix that is NOT one silently eats the wrong number of
    // characters and builds a redirect to a mangled path. Unreachable today —
    // the pane's scope is injected from the same request path it was served
    // on — so this is the assertion that makes a future proxy change redden
    // instead of quietly corrupting a link.
    const scope = scopePrefix();
    const rawPath = window.location.pathname.startsWith(scope)
      ? window.location.pathname.slice(scope.length)
      : window.location.pathname;
    const target = isRootSideDoor(location)
      ? STUDIO_BASE
      : STUDIO_BASE + rawPath + window.location.search + window.location.hash;
    return <Redirect to={target} replace />;
  }
  // From here down the studio owns the URL. The nested base means every route
  // and every navigate() inside keeps its historical spelling — wouter appends
  // this base to the parent's, so a workspace pane resolves to
  // /x/<id>/studio/… with nothing else to change.
  return (
    <Router base={STUDIO_BASE}>
      <StudioShell activeRole={activeRole} isRestricted={isRestricted} />
    </Router>
  );
}

// StudioShell picks the shell the operator's role earns, and — the reason it
// is a component rather than a ternary inline above — titles the document for
// ALL THREE of them.
//
// useDocumentTitle had exactly one call site, inside AuthedApp, so a
// `config_editor` and a submitter read the PRODUCT title in their tab: the
// static one from studio/index.html, which is written for a crawler reading
// the home. It has to be called INSIDE the router, not in AuthGate, because it
// matches on a base-relative location.
function StudioShell({
  activeRole,
  isRestricted,
}: {
  activeRole: string | null;
  isRestricted: boolean;
}) {
  useDocumentTitle();
  return (
    <>
      {
        // The least-privilege `config_editor` role gets a limited shell that
        // can ONLY edit the team's config-shares — no Sidebar, no
        // runs/board/launch. It's a real team member (isRestricted is false
        // for them), so this branch must sit ABOVE the isRestricted check. The
        // auth side-doors above (/invitations/accept, /cli-auth, /config/:id)
        // stay reachable for them.
        activeRole === "config_editor" ? (
          <Suspense fallback={<BootLoading />}>
            <ConfigEditorShell />
          </Suspense>
        ) : isRestricted ? (
          // The "submitter" tier (signed in, no team, not super-admin) gets a
          // marketplace-only shell instead of the full studio.
          <Suspense fallback={<BootLoading />}>
            <RestrictedShell />
          </Suspense>
        ) : (
          <AuthedApp />
        )
      }
    </>
  );
}

function AuthedApp() {
  const [location] = useLocation();
  const { isDesktop, ready, firstRunPending, refresh, pickAndAddProject } =
    useDesktop();
  const serverInfo = useServerInfoStore((s) => s.info);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<string>("api-keys");
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Set to a cloud connection id when its session expires (cloud:auth-expired);
  // opens the re-login modal.
  const [cloudReloginConnId, setCloudReloginConnId] = useState<string | null>(null);

  // Titled by StudioShell above, which covers the two role shells too.
  // Reset SPA state on a server-side project hot-swap so the new
  // project's empty home view replaces whatever the user was looking
  // at. No-op in desktop (server-mode WS) and cloud modes.
  useProjectSwitchListener();
  // Scope run/editor tabs to the active project (hide other projects'
  // tabs instead of leaking them across a switch).
  useProjectScopeSync();

  useEffect(() => {
    const offs = [
      onDesktopEvent(DesktopEvent.MenuSettings, () => {
        setSettingsTab("api-keys");
        setSettingsOpen(true);
      }),
      onDesktopEvent(DesktopEvent.MenuSwitchProject, () => setSwitcherOpen(true)),
      // MenuNewProject opens the native directory picker directly —
      // previously it opened the switcher (same as MenuSwitchProject),
      // which forced users through an extra step. The picker is also
      // what the "+ Add project…" button inside the switcher uses.
      onDesktopEvent(DesktopEvent.MenuNewProject, () => {
        void pickAndAddProject();
      }),
      onDesktopEvent(DesktopEvent.MenuAbout, () => {
        setSettingsTab("about");
        setSettingsOpen(true);
      }),
      // Menu undo/redo route to the active editor tab's per-tab
      // document store. With multi-tab editors, a global singleton
      // would mutate the wrong document; the registry lookup keeps
      // the action scoped to whatever the user is looking at.
      onDesktopEvent(DesktopEvent.MenuUndo, () => activeEditorDocStore()?.getState().undo()),
      onDesktopEvent(DesktopEvent.MenuRedo, () => activeEditorDocStore()?.getState().redo()),
      // Native OS notification for run-health alerts. No-op in browser
      // mode (onDesktopEvent returns a noop unsubscribe there).
      onDesktopEvent<RunAlertPayload>(DesktopEvent.RunAlert, (payload) =>
        showRunAlertNotification(payload),
      ),
      // A cloud connection's session expired (silent refresh rejected) —
      // prompt re-login for that connection. Payload is the connection id.
      onDesktopEvent<string>(DesktopEvent.CloudAuthExpired, (connId) =>
        setCloudReloginConnId(connId),
      ),
    ];
    // Listen for the SPA-emitted open-switcher event from ProjectLabel
    // (clicking the project chip in the toolbar / run header). Cloud has no
    // project concept and never mounts ProjectSwitcher — no listener there so
    // stray dispatches don't try to open an unmounted dialog.
    const onOpenSwitcher = () => setSwitcherOpen(true);
    if (serverInfo?.mode !== "cloud") {
      window.addEventListener("iterion:open-project-switcher", onOpenSwitcher);
    }
    // Sidebar Settings button (and any other SPA caller) dispatches this
    // to surface the dialog. The optional `tab` detail lets callers
    // land on a specific section (Appearance, Backends, …).
    const onOpenSettings = (e: Event) => {
      const detail = (e as CustomEvent<{ tab?: string }>).detail;
      if (detail?.tab) setSettingsTab(detail.tab);
      else setSettingsTab(isDesktop ? "api-keys" : "appearance");
      setSettingsOpen(true);
    };
    window.addEventListener("iterion:open-settings", onOpenSettings as EventListener);
    // In a workspace pane, native menu events reach the main frame, not this
    // iframe — the shell FORWARDS the connection-scoped ones (Settings / About /
    // Undo / Redo) via postMessage. Translate them into the same local actions.
    const onShellMenu = (e: MessageEvent) => {
      const d = e.data as { source?: string; type?: string; menu?: string; tab?: string } | null;
      if (d?.source !== "iterion-shell" || d.type !== "menu") return;
      switch (d.menu) {
        case "settings":
          setSettingsTab(d.tab || (isDesktop ? "api-keys" : "appearance"));
          setSettingsOpen(true);
          break;
        case "undo":
          activeEditorDocStore()?.getState().undo();
          break;
        case "redo":
          activeEditorDocStore()?.getState().redo();
          break;
      }
    };
    window.addEventListener("message", onShellMenu);
    return () => {
      offs.forEach((off) => off());
      window.removeEventListener("iterion:open-project-switcher", onOpenSwitcher);
      window.removeEventListener("iterion:open-settings", onOpenSettings as EventListener);
      window.removeEventListener("message", onShellMenu);
    };
  }, [pickAndAddProject, isDesktop, serverInfo?.mode]);

  useEffect(() => {
    // Ctrl/Cmd+P opens the ProjectSwitcher — a folder concept that only
    // exists in local/desktop. Skipping the shortcut in cloud avoids the
    // silent no-op where the dialog isn't mounted.
    const isCloud = serverInfo?.mode === "cloud";
    const onKey = (e: KeyboardEvent) => {
      if (!isCloud && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setSwitcherOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === ",") {
        e.preventDefault();
        setSettingsTab("api-keys");
        setSettingsOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [serverInfo?.mode]);

  if (!ready) {
    return (
      <BootLoading />
    );
  }

  if (isDesktop && firstRunPending) {
    return (
      <Suspense
        fallback={
          <BootLoading />
        }
      >
        <Welcome onComplete={refresh} />
      </Suspense>
    );
  }

  return (
    // AssistantProvider sits ABOVE the route tree: the assistant's
    // session is mounted once for the whole authenticated app, so
    // navigating between routes can neither restart it nor drop the
    // transcript. It also isolates that session in its own run store —
    // see the file header there.
    <AssistantProvider>
      {isDesktop && <MissingCLIBanner />}
      <AppShell>
        {/* Catch-all under every per-route boundary. Routes mounted with
            `component={…}` (/account, /orgs/:id, /teams/:id, /integrations*,
            /admin/*) have none of their own, and without this their throw
            would travel up to AssistantProvider's boundary — whose fallback
            re-renders these same children, so a deterministic throw would
            re-throw inside the fallback and escape past the root, where
            nothing catches it. Contain it here and that boundary only ever
            sees the assistant's own failures. */}
        <ErrorBoundary area="Page" resetKey={location}>
          <Switch>
          <Route path="/runs/new">
            <ErrorBoundary area="Launch view">
              <LaunchDocStoreProvider>
                <LaunchView />
              </LaunchDocStoreProvider>
            </ErrorBoundary>
          </Route>
          <Route path="/runs/:id">
            <ErrorBoundary area="Run view">
              <RunsTabsView />
            </ErrorBoundary>
          </Route>
          <Route path="/runs">
            <ErrorBoundary area="Runs list">
              <RunsTabsView />
            </ErrorBoundary>
          </Route>
          {/* Order matters: the literal /bots/new must win over /bots/:name. */}
          <Route path="/bots/new">
            <ErrorBoundary area="Bot builder view">
              <BotBuilderView />
            </ErrorBoundary>
          </Route>
          <Route path="/bots/:name">
            <ErrorBoundary area="Bot home view">
              <BotHomeView />
            </ErrorBoundary>
          </Route>
          <Route path="/bots">
            <ErrorBoundary area="Bots view">
              <BotsView />
            </ErrorBoundary>
          </Route>
          <Route path="/account" component={SettingsPage} />
          <Route path="/orgs/:id" component={OrgPage} />
          <Route path="/teams/:id" component={TeamPage} />
          <Route path="/integrations/connect" component={ConnectRepoWizardView} />
          <Route path="/integrations/bind" component={BindBotWizardView} />
          <Route path="/integrations" component={IntegrationsPage} />
          <Route path="/repos/:key">
            <ErrorBoundary area="Repository view">
              <RepoDetailView />
            </ErrorBoundary>
          </Route>
          {/* The whole admin console behind one authorization gate
              (RequireSuperAdmin). `nest` makes the inner paths relative to
              /admin; each page keeps its own super-admin/cloud check as
              defense-in-depth. The screens come from the console's route
              table, which AdminNav renders as its sections. */}
          <Route path={ADMIN_BASE} nest>
            <RequireSuperAdmin>
              <AdminConsoleRoutes />
            </RequireSuperAdmin>
          </Route>
          {serverInfo?.native_tracker_enabled ? (
            <Route path="/board/labels">
              <ErrorBoundary area="Board labels view">
                <LabelsView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/board/labels">
              <FeatureUnavailable
                title="Board labels"
                description="Label vocabulary of the native kanban tracker."
                reason="The native kanban tracker isn't wired on this server."
                ctaLabel="Open Runs"
                ctaHref="/runs"
              />
            </Route>
          )}
          {serverInfo?.native_tracker_enabled ? (
            <Route path="/board/fields">
              <ErrorBoundary area="Board fields view">
                <FieldsView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/board/fields">
              <FeatureUnavailable
                title="Board fields"
                description="Custom card fields of the native kanban tracker."
                reason="The native kanban tracker isn't wired on this server."
                ctaLabel="Open Runs"
                ctaHref="/runs"
              />
            </Route>
          )}
          <Route path="/insights">
            <ErrorBoundary area="Runs analytics view">
              <RunsAnalyticsView />
            </ErrorBoundary>
          </Route>
          {serverInfo?.plugins_enabled ? (
            <Route path="/plugins">
              <ErrorBoundary area="Plugins view">
                <PluginsView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/plugins">
              <FeatureUnavailable
                title="Plugins"
                description="Installable packages contributing rewriters (command-output compression), MCP servers, skills, and hooks."
                reason="This server doesn't expose the plugin registry."
                ctaHint="Plugins are managed on the server host with the `iterion plugin` CLI."
              />
            </Route>
          )}
          {serverInfo?.secrets_enabled ? (
            <Route path="/secrets">
              <ErrorBoundary area="Secrets view">
                <SecretsView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/secrets">
              <FeatureUnavailable
                title="Secrets"
                description="Local sealed secret store — API keys and named secrets resolved into runs."
                reason="The local secret store lives on the studio host (sealed with the OS keychain), so it isn't available on this server."
                ctaHint="In cloud mode, manage secrets from your team page's Secrets tab; locally, use `iterion secret set|list|rm`."
              />
            </Route>
          )}
          {serverInfo?.skills_enabled ? (
            <Route path="/skills">
              <ErrorBoundary area="Skills view">
                <SkillsView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/skills">
              <FeatureUnavailable
                title="Skills"
                description="Operator-curated skill library referenced by workflows via the DSL `skills:` field."
                reason="The skill library lives on the studio host's filesystem, so it's only available in local (non-cloud) mode."
                ctaLabel="Open Bots"
                ctaHref="/bots"
                ctaHint="Bots still ship their own bundled skills — browse them from the catalog."
              />
            </Route>
          )}
          {serverInfo?.native_tracker_enabled ? (
            <Route path="/pipelines">
              <ErrorBoundary area="Pipeline board view">
                <PipelineBoardView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/pipelines">
              <FeatureUnavailable
                title="Pipelines"
                description="Global control-center board tracking staged tasks and their in-flight runs."
                reason="The pipeline board sits on the native kanban tracker, which isn't wired on this server."
                ctaLabel="Open Runs"
                ctaHref="/runs"
                ctaHint="Runs launched on this server remain visible in the Runs view."
              />
            </Route>
          )}
          {serverInfo?.native_tracker_enabled ? (
            <Route path="/board">
              <ErrorBoundary area="Board view">
                <BoardView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/board">
              <FeatureUnavailable
                title="Board"
                description="Native kanban tracker — issues, labels, and drag-and-drop states feeding the dispatcher."
                reason="The native kanban tracker isn't wired on this server."
                ctaLabel="Open Runs"
                ctaHref="/runs"
                ctaHint="Work dispatched on this server still shows up in the Runs view."
              />
            </Route>
          )}
          {serverInfo?.dispatcher_enabled ? (
            <Route path="/dispatcher">
              <ErrorBoundary area="Dispatcher view">
                <DispatcherView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/dispatcher">
              <FeatureUnavailable
                title="Dispatcher"
                description="Long-running dispatcher: polls an issue tracker and launches a workflow per eligible issue."
                ctaLabel="Open Pipelines"
                ctaHref="/pipelines"
                ctaHint="The pipeline board tracks staged tasks and their in-flight runs on this server."
              />
            </Route>
          )}
          {/* Not gated on triggers_enabled: the view handles a missing
              trigger store per tab, and cloud servers carry Schedules
              even when the event-trigger spine is off. */}
          <Route path="/triggers">
            <ErrorBoundary area="Automations view">
              <TriggersView />
            </ErrorBoundary>
          </Route>
          {serverInfo?.marketplace_enabled ? (
            <Route path="/marketplace">
              <ErrorBoundary area="Marketplace view">
                <MarketplaceView />
              </ErrorBoundary>
            </Route>
          ) : (
            <Route path="/marketplace">
              <FeatureUnavailable
                title="Marketplace"
                description="Hosted registry of shareable bots and plugins."
                ctaLabel="Open Bots"
                ctaHref="/bots"
                ctaHint="Browse the bots already discoverable in this workspace."
              />
            </Route>
          )}
          <Route path="/editor">
            <ErrorBoundary area="Editor view">
              <EditorTabsView />
            </ErrorBoundary>
          </Route>
          <Route path="/whats-next">
            <ErrorBoundary area="What's Next view">
              <WhatsNextView />
            </ErrorBoundary>
          </Route>
          <Route path="/sitemap">
            <ErrorBoundary area="Sitemap view">
              <SitemapPage />
            </ErrorBoundary>
          </Route>
          <Route path="/config-editor">
            <ErrorBoundary area="Config editor view">
              <ConfigEditorView />
            </ErrorBoundary>
          </Route>
          <Route path="/" component={HomeView} />
          <Route component={HomeView} />
          </Switch>
        </ErrorBoundary>
      </AppShell>
      <ToastContainer />
      <GlobalCommandPalette />
      {/* Mounted here, outside the <Route> tree, for the same reason as
          the command palette: it must survive navigation by
          construction. Reachable from every authenticated route.

          Its own boundary, because that mount point is also outside
          every per-route <ErrorBoundary>: a throw in the dock would
          otherwise propagate past AppShell and unmount the entire
          authenticated app, so the operator would lose /board and
          /runs because the assistant choked on a transcript. The
          fallback is nothing at all — a corner surface that crashed
          should disappear, not paint an error card over the page. */}
      <ErrorBoundary
        area="Assistant dock"
        // Navigating retries the dock. Without a reset key the custom
        // fallback replaces the default card — "Try again" included — so a
        // single bad transcript would have cost the operator the assistant
        // for the rest of the browser session.
        resetKey={location}
        // Releases the 380px column on the way out. AppShell reserves it
        // from the dock STATE, which lives in a context the boundary does
        // not touch: a dock that crashed while `docked-right` left a
        // permanent dead band down every page, with no control to clear it
        // (the minimise button was inside the crashed dock). Same failure
        // as 505f2aeb, reached through the error path.
        fallback={<AssistantDockCrashed />}
      >
        <ChatDock />
      </ErrorBoundary>
      {/* Settings + ProjectSwitcher are also lazy and need their own
          Suspense boundary because they unmount/remount on open/close. */}
      <Suspense fallback={null}>
        <SettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          tab={settingsTab}
          onTabChange={setSettingsTab}
          desktopFeatures={isDesktop}
        />
        {/* ProjectSwitcher renders in desktop and local-server modes only —
            cloud has no work_dir concept and the Ctrl+P shortcut plus every
            open-switcher dispatch are gated on non-cloud, so the dialog
            never needs to mount there. */}
        {serverInfo?.mode !== "cloud" && (
          <ProjectSwitcher open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
        )}
        <CloudReloginModal
          connId={cloudReloginConnId}
          onClose={() => setCloudReloginConnId(null)}
        />
      </Suspense>
    </AssistantProvider>
  );
}
